import React, { useEffect, useRef, useState } from "react"

import {
  faMicrophone,
  faMicrophoneSlash,
} from "@fortawesome/free-solid-svg-icons"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  EllipsisVerticalIcon,
  LockClosedIcon,
  LockOpenIcon,
} from "@heroicons/react/24/outline"
import { Subscription } from "@rails/actioncable"
import Tippy from "@tippyjs/react"

import { RosterEntry } from "components/AudioRoom/useRoster"

type StudentActionsProps = {
  member: RosterEntry
  channel: Subscription
}

const StudentActions: React.FC<StudentActionsProps> = ({ member, channel }) => {
  const controlsLocked = member.controlsLocked ?? false
  const [menuOpen, setMenuOpen] = useState(false)
  const [confirmRemoval, setConfirmRemoval] = useState(false)
  const menuRef = useRef<HTMLDivElement>(null)
  const isUnmuted = member.muted === false
  const userId = member.userId

  useEffect(() => {
    if (!menuOpen) {
      setConfirmRemoval(false)
      return
    }

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false)
      }
    }

    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [menuOpen])

  if (!userId) return null

  const handleControlsLockToggle = () => {
    channel.perform(controlsLocked ? "unlock_user" : "lock_user", { userId })
    setMenuOpen(false)
  }

  const handleMuteToggle = () => {
    channel.perform(isUnmuted ? "mute_user" : "unmute_user", { userId })
    setMenuOpen(false)
  }

  const handleRemoveClick = () => {
    if (!confirmRemoval) {
      setConfirmRemoval(true)
      return
    }

    channel.perform("remove_student", { userId })
    setMenuOpen(false)
  }

  return (
    <div ref={menuRef} className="relative flex flex-none items-center gap-1">
      <Tippy content={controlsLocked ? "Controls locked" : "Controls unlocked"}>
        <button
          type="button"
          onClick={handleControlsLockToggle}
          className="rounded border-none bg-transparent p-0"
          aria-label={controlsLocked ? "Unlock controls" : "Lock controls"}
        >
          {controlsLocked ? (
            <LockClosedIcon className="h-5 w-5 text-gray-400" />
          ) : (
            <LockOpenIcon className="h-5 w-5 text-gray-400" />
          )}
        </button>
      </Tippy>
      <Tippy content={isUnmuted ? "Unmuted" : "Muted"}>
        <button
          type="button"
          onClick={handleMuteToggle}
          className="rounded border-none bg-transparent p-0"
          aria-label={isUnmuted ? "Mute" : "Unmute"}
        >
          <FontAwesomeIcon
            icon={isUnmuted ? faMicrophone : faMicrophoneSlash}
            className={isUnmuted ? "text-green-500" : "text-gray-400"}
          />
        </button>
      </Tippy>
      <button
        type="button"
        onClick={() => setMenuOpen(open => !open)}
        className="rounded border-none bg-transparent p-0.5 text-white/60 hover:text-white"
        aria-label="Student controls"
      >
        <EllipsisVerticalIcon className="h-5 w-5 text-gray-400" />
      </button>
      {menuOpen && (
        <div className="absolute right-0 top-full z-10 mt-1 min-w-[10rem] rounded border border-white/20 bg-neutral-900 py-1 text-sm shadow-lg">
          <button
            type="button"
            className="block w-full border-none bg-transparent px-3 py-1.5 text-left text-white hover:bg-white/10"
            onClick={handleMuteToggle}
          >
            {isUnmuted ? "Mute" : "Unmute"}
          </button>
          <button
            type="button"
            className="block w-full border-none bg-transparent px-3 py-1.5 text-left text-white hover:bg-white/10"
            onClick={handleControlsLockToggle}
          >
            {controlsLocked ? "Unlock controls" : "Lock controls"}
          </button>
          <button
            type="button"
            className={`block w-full border-none px-3 py-1.5 text-left text-white ${
              confirmRemoval ? "hover:bg-green-600" : "hover:bg-red-600"
            }`}
            onClick={handleRemoveClick}
          >
            {confirmRemoval ? "Confirm removal" : `Remove ${member.fullName}`}
          </button>
        </div>
      )}
    </div>
  )
}

export default StudentActions
